import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Alert, Container, Row } from "reactstrap";

import BreadCrumb from "Components/Common/BreadCrumb";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import { getActivityReport } from "common/services/analytics.service";

import ReportFilters, {
  AGENCY_ALL_EXCEPT_RMU,
  DEFAULT_FILTERS,
  isAllAgencies,
} from "./ReportFilters";
import SummaryTiles from "./SummaryTiles";
import UsageTrend from "./UsageTrend";
import AdoptionAndStickiness from "./AdoptionAndStickiness";
import WhenUsed from "./WhenUsed";
import DueDiligenceSearches from "./DueDiligenceSearches";
import AgencyEngagement from "./AgencyEngagement";
import MostActiveUsers from "./MostActiveUsers";
import TopPages from "./TopPages";
import MostViewedRecords from "./MostViewedRecords";
import {
  ActivityReportData,
  ActivityReportMeta,
  ReportFilterValue,
} from "./types";

/**
 * The calendar date the viewer picked, as they see it — `en-CA` renders local
 * time as YYYY-MM-DD. The backend anchors it to midnight in `browserTimeZone`,
 * so picking "26 Aug" means their 26 Aug, start to finish.
 */
const toApiDate = (date: Date | null) =>
  date ? new Date(date).toLocaleDateString("en-CA") : undefined;

/**
 * Whoever is reading the report should see their own clock, not the server's.
 * Every day boundary, trend bucket and heatmap hour is computed in this zone.
 */
const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

/**
 * The Activity Report is served by a single endpoint: one fetch per filter
 * change feeds every widget below, so the whole page always shows one
 * consistent snapshot rather than each card carrying its own period.
 */
const ActivityReport = () => {
  const location = useLocation();
  usePageActivity(location.pathname, "Activity Report");

  const [filters, setFilters] = useState<ReportFilterValue>({
    ...DEFAULT_FILTERS,
  });
  const [data, setData] = useState<ActivityReportData | null>(null);
  const [meta, setMeta] = useState<ActivityReportMeta | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    let cancelled = false;

    const fetchReport = async () => {
      setLoading(true);
      setError("");

      const usingCustomRange = Boolean(filters.fromDate && filters.toDate);

      try {
        const response: any = await getActivityReport({
          period: filters.period,
          ...(usingCustomRange
            ? {
                startDate: toApiDate(filters.fromDate),
                endDate: toApiDate(filters.toDate),
              }
            : {}),
          // The agency filter carries three cases: a real agency id, every
          // agency, or every agency bar the RMU — the last being the default,
          // and the only one that needs telling the server anything extra.
          ...(isAllAgencies(filters.agency)
            ? {}
            : { agency: filters.agency }),
          ...(filters.agency === AGENCY_ALL_EXCEPT_RMU
            ? { rmu: "exclude" }
            : {}),
          ...(filters.page ? { page: filters.page } : {}),
          ...(filters.user ? { user: filters.user } : {}),
          ...(browserTimeZone() ? { timezone: browserTimeZone() } : {}),
          limit: 10,
        });

        if (cancelled) return;

        if (!response?.data) {
          setData(null);
          setMeta(null);
          setError("The activity report could not be loaded.");
          return;
        }

        setData(response.data);
        setMeta(response.meta || null);
      } catch (e) {
        if (cancelled) return;
        setData(null);
        setMeta(null);
        setError("The activity report could not be loaded.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReport();

    return () => {
      cancelled = true;
    };
  }, [filters]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Activity Report"
            pageTitles={[
              { title: "Dashboard", link: "/dashboard" },
              { title: "User Activity", link: "#" },
            ]}
          />

          <ReportFilters
            value={filters}
            onChange={setFilters}
            meta={meta}
            loading={loading}
          />

          {error ? (
            <Alert color="danger" fade={false} className="mb-4">
              {error}
            </Alert>
          ) : null}

          <Row>
            <SummaryTiles
              data={data}
              comparable={Boolean(meta?.comparable)}
              loading={loading}
            />
          </Row>

          <Row>
            <UsageTrend data={data} loading={loading} />
            <AdoptionAndStickiness data={data} />
          </Row>

          <Row>
            <WhenUsed data={data} loading={loading} timezone={meta?.timezone} />
          </Row>

          <Row>
            <DueDiligenceSearches
              data={data}
              granularity={meta?.granularity || "day"}
              comparable={Boolean(meta?.comparable)}
              pageFiltered={Boolean(filters.page)}
              loading={loading}
            />
          </Row>

          <Row>
            <AgencyEngagement data={data} loading={loading} />
            <MostActiveUsers data={data} loading={loading} />
          </Row>

          <Row>
            <TopPages data={data} loading={loading} />
            <MostViewedRecords
              data={data}
              contentTypes={meta?.contentTypes || []}
              limit={meta?.limit || 10}
              loading={loading}
            />
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ActivityReport;
